import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import "./styles/global.css";
import "./styles/free-badge.css";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import SEO from "./components/SEO";
import StructuredData from "./components/StructuredData";
import RouteTracker from "./components/RouteTracker";
import { SEO_CONSTANTS } from "./common/SEOConstants";
import { Helmet } from "react-helmet-async";
// Import Layout Components
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Import Section Components
import Hero from "./components/sections/Hero";
import About from "./components/sections/About";
import Services from "./components/sections/Services";
import Projects from "./components/sections/Projects";
import Contact from "./components/sections/Contact";
import Testimonials from "./components/sections/Testimonials";
import WhyFree from "./components/sections/WhyFree";

// Import Pages
import Blog from "./pages/Blog";
import NotFound from "./pages/NotFound";
import Register from "./pages/Register";
import Login from "./pages/Login";
import VerificationSuccess from "./pages/VerificationSuccess";
import Dashboard from "./pages/Dashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SchoolManagementSystem from "./pages/SchoolManagementSystem";
import CoachingManagementSystem from "./pages/CoachingManagementSystem";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import UserGuide from "./pages/UserGuide";
import ContactUs from "./pages/ContactUs";
import CancellationAndRefund from "./pages/CancellationAndRefund";
import Pricing from "./pages/Pricing";
// ScrollToTop component to handle scroll restoration
const ScrollToTop = () => {
  const { pathname, hash, state } = useLocation();

  useEffect(() => {
    if (state && state.scrollToSection) {
      setTimeout(() => {
        const element = document.getElementById(state.scrollToSection);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } 
    // Second, handle hash-based scrolling (from URL)
    else if (hash) {
      // Remove the # character
      const id = hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
    // Otherwise, scroll to top
    else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, state]);

  return null;
};

// Home Page Component with sections
const HomePage = () => {
  // Home page specific structured data
  const homePageStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Pathshala Sanchalak - School Management Software",
    url: "https://pathshalasanchalak.in",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate:
          "https://pathshalasanchalak.in/search?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
    description:
      "Complete school and coaching management system for educational institutions. Manage students, faculty, courses, and more.",
    audience: {
      "@type": "Audience",
      audienceType: "Educational Institutions",
    },
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(homePageStructuredData)}
        </script>
      </Helmet>
      <Hero />
      <About />
      <Services />
      <WhyFree />
      {/* <Testimonials /> */}
      <Contact />
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const location = useLocation();

  // Define SEO metadata based on route
  const getSEOData = () => {
    const path = location.pathname;

    // Default SEO data
    let seoData = {
      title: SEO_CONSTANTS.DEFAULT_TITLE,
      description: SEO_CONSTANTS.DEFAULT_DESCRIPTION,
      keywords: SEO_CONSTANTS.getCombinedKeywords(),
      canonicalUrl: path,
      ogType: "website",
    };

    // Route-specific SEO data
    if (path === "/") {
      seoData = {
        ...seoData,
        title: SEO_CONSTANTS.DEFAULT_TITLE,
        description: SEO_CONSTANTS.DEFAULT_DESCRIPTION,
        keywords: SEO_CONSTANTS.getCombinedKeywords(
          SEO_CONSTANTS.HOMEPAGE_KEYWORDS
        ),
      };
    } else if (path === "/register") {
      seoData = {
        ...seoData,
        title: "Register - Pathshala Sanchalak",
        description:
          "Create your account to start managing your educational institution effectively with Pathshala Sanchalak.",
        keywords: SEO_CONSTANTS.getCombinedKeywords([
          "register school management",
          "create coaching account",
          "school management registration",
        ]),
      };
    } else if (path === "/login") {
      seoData = {
        ...seoData,
        title: "Login - Pathshala Sanchalak",
        description:
          "Log in to your Pathshala Sanchalak dashboard to manage your educational institution.",
        keywords: SEO_CONSTANTS.getCombinedKeywords([
          "login school management",
          "coaching software login",
          "education management login",
        ]),
      };
    } else if (path === "/blog") {
      seoData = {
        ...seoData,
        title: "Education Management Blog - Pathshala Sanchalak",
        description:
          "Read the latest articles, tips and best practices for managing educational institutions effectively.",
        keywords: SEO_CONSTANTS.getCombinedKeywords(
          SEO_CONSTANTS.BLOG_KEYWORDS
        ),
      };
    } else if (path.startsWith("/project/")) {
      seoData = {
        ...seoData,
        title: "Project Details - Pathshala Sanchalak",
        description:
          "Learn about our successful implementations and features of our school management system.",
        keywords: SEO_CONSTANTS.getCombinedKeywords([
          "school management case study",
          "education software implementation",
          "coaching management projects",
        ]),
      };
    } else if (path === "/school-management-system") {
      seoData = {
        ...seoData,
        title: "School Management System - Free & Complete Solution | Pathshala Sanchalak",
        description: "Best school management system for effortless administration. Manage students, faculty, employees, attendance, fees & more in one integrated platform. Start free now!",
        keywords: SEO_CONSTANTS.getCombinedKeywords([
          "school management system",
          "school erp",
          "school administration software",
          "student information system",
          "school management software",
          "education management system"
        ]),
      };
    } else if (path === "/coaching-management-system") {
      seoData = {
        ...seoData,
        title: "Coaching Management System - Free & Complete Solution for Institutes | Pathshala Sanchalak",
        description: "Best coaching management system for coaching institutes & tuition centers. Manage students, faculty, employees, fees & more in one integrated platform. Try free today!",
        keywords: SEO_CONSTANTS.getCombinedKeywords([
          "coaching management system",
          "coaching institute software",
          "tuition center management",
          "coaching class software",
          "test preparation management system"
        ]),
      };
    } else if (path === "/contact-us") {
      seoData = {
        ...seoData,
        title: "Contact Us - Pathshala Sanchalak | Get Support & Demo",
        description: "Contact Pathshala Sanchalak for technical support, demos, and inquiries about our free school and coaching management system. We're here to help you succeed.",
        keywords: SEO_CONSTANTS.getCombinedKeywords([
          "contact pathshala sanchalak",
          "school management support",
          "coaching software help",
          "education management contact",
          "technical support",
          "demo request"
        ]),
      };
    } else if (path === "/pricing") {
      seoData = {
        ...seoData,
        title: "Pricing - Pathshala Sanchalak | Flexible Plans",
        description: "Flexible pricing for institutes, tutors, and creators. Start free or choose white-labeled plans from ₹600/month.",
        keywords: SEO_CONSTANTS.getCombinedKeywords([
          "school management pricing",
          "coaching software pricing",
          "lms white label pricing",
          "pathshala sanchalak pricing"
        ]),
      };
    }

    return seoData;
  };

  const seoData = getSEOData();

  return (
    <div className="App">
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl={seoData.canonicalUrl}
        ogType={seoData.ogType}
      />
      <StructuredData />
      <ScrollToTop />
      <RouteTracker />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/verify" element={<VerificationSuccess />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/school-management-system" element={<SchoolManagementSystem />} />
          <Route path="/coaching-management-system" element={<CoachingManagementSystem />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/cancellation-refund" element={<CancellationAndRefund />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/user-guide" element={<UserGuide />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
